import { email, required, useTranslate } from "ra-core";
import type { FocusEvent, ClipboardEventHandler } from "react";
import { useFormContext } from "react-hook-form";
import { useState, useEffect, useMemo } from "react";
import { Separator } from "@/components/ui/separator";
import { useIsMobile } from "@/hooks/use-mobile";
import { TextInput } from "@/components/admin/text-input";
import { DateInput } from "@/components/admin/date-input";
import { RadioButtonGroupInput } from "@/components/admin/radio-button-group-input";
import { SelectInput } from "@/components/admin/select-input";
import { AutocompleteInput } from "@/components/admin/autocomplete-input";
 

import { useConfigurationContext } from "../root/ConfigurationContext";
import { Avatar } from "./Avatar";
import { formatDocument, isValidCnpj, isValidCpf, stripNonDigits } from "./document-validation";
import { CamposDePerfilCompleto } from "../crm/camposDoLead";
import { MENSAGEM_SEM_CELULAR, normalizarCelular } from "./gravarContato";
import { MoedaFormInput } from "@/components/admin/moeda-input";

type ContactInputsProps = {
  requireDocumentAndAddress?: boolean;
};

export const ContactInputs = ({
  requireDocumentAndAddress = true,
}: ContactInputsProps) => {
  const isMobile = useIsMobile();

  return (
    <div className="flex flex-col gap-4 p-1 md:p-2">
      <div className="flex justify-center md:justify-start">
        <Avatar />
      </div>
      <div className={`flex gap-6 ${isMobile ? "flex-col" : "flex-row"}`}>
        <div className="flex flex-col gap-6 lg:gap-10 flex-1">
          <ContactIdentityInputs requireDocumentAndAddress={requireDocumentAndAddress} />
          <ContactAddressInputs requireDocumentAndAddress={requireDocumentAndAddress} />
        </div>
        <Separator
          orientation={isMobile ? "horizontal" : "vertical"}
          className="flex-shrink-0"
        />
        <div className="flex flex-col gap-6 lg:gap-10 flex-1">
          <ContactPersonalInformationInputs requireDocumentAndAddress={requireDocumentAndAddress} />
          {/* O MESMO componente que o formulário de novo lead monta. Ficha de
              contato e lead são a mesma pessoa: manter dois formulários pedindo
              profissão, família e patrimônio garantiria duas respostas
              diferentes para a mesma pergunta. Ver crm/camposDoLead.tsx. */}
          <CamposDePerfilCompleto />
          <ContactMiscInputs />
        </div>
      </div>
    </div>
  );
};


const ContactIdentityInputs = ({ requireDocumentAndAddress }: { requireDocumentAndAddress: boolean }) => {
  const translate = useTranslate();
  return (
    <div className="flex flex-col gap-4">
      <h6 className="text-lg font-semibold">
        {translate("atomic.contacts.identity")}
      </h6>
      <GeneroInput />
      <ContactDocumentInputs requireDocumentAndAddress={requireDocumentAndAddress} />
      <CargoInput />
      <EmpresaInput />
      <EstadoCivilInput />
    </div>
  );
};

/*
 * As peças abaixo são exportadas uma a uma para a jornada de cadastro
 * e de edição (`passosDaFicha.tsx`), que mostra poucos campos por passo.
 */

export const GeneroInput = () => {
  const { contactGender } = useConfigurationContext();
  return (
    <RadioButtonGroupInput
      label={false}
      row
      source="details.gender"
      choices={contactGender}
      helperText={false}
      optionText="label"
      optionValue="value"
      defaultValue={contactGender[0].value}
    />
  );
};

/** Tipo, documento, nome e nascimento: o que identifica a pessoa. */
export const ContactDocumentInputs = ({ requireDocumentAndAddress }: { requireDocumentAndAddress: boolean }) => {
  const { watch } = useFormContext();
  const personType = watch("details.person_type") || "PF";
  const isCompany = personType === "PJ";
  const documentLabel = isCompany ? "CNPJ" : "CPF";
  const documentPlaceholder = isCompany ? "00.000.000/0000-00" : "000.000.000-00";
  const documentMaxLength = isCompany ? 18 : 14;

  const validateDocument = (value: string) => {
    const digits = String(value || "").replace(/\D/g, "");
    if (!digits) return undefined;
    if (personType === "PJ") {
      if (digits.length !== 14) return "CNPJ inválido";
      return isValidCnpj(digits) ? undefined : "CNPJ inválido";
    }
    if (digits.length !== 11) return "CPF inválido";
    return isValidCpf(digits) ? undefined : "CPF inválido";
  };

  const birthDateLabel = isCompany ? "Data de Criação" : "Data de Nascimento";

  return (
    <>
      <SelectInput
        source="details.person_type"
        label="Tipo"
        choices={personTypeChoices}
        optionText="name"
        optionValue="id"
        helperText={false}
        validate={required()}
        defaultValue="PF"
      />
      <TextInput 
        source="details.cpf" 
        label={documentLabel}
        validate={requireDocumentAndAddress ? [required(), validateDocument] : [validateDocument]} 
        helperText={false}
        placeholder={documentPlaceholder}
        inputMode="numeric"
        maxLength={documentMaxLength}
        format={(value: string) => formatDocument(value, personType)}
        parse={(value: string) => stripNonDigits(value)}
      />
      <TextInput source="details.name" label="Nome" validate={required()} helperText={false} />
      <DateInput 
        source="details.birth_date" 
        label={birthDateLabel}
        validate={requireDocumentAndAddress ? required() : undefined} 
        helperText={false}
        manualEntry
      />
    </>
  );
};

export const CargoInput = () => <TextInput source="details.title" label="Cargo" helperText={false} />;

export const EmpresaInput = () => <TextInput source="details.company" label="Empresa" helperText={false} />;

export const EstadoCivilInput = () => (
  <SelectInput
    source="details.marital_status"
    label="Estado Civil"
    choices={maritalStatusChoices}
    helperText={false}
  />
);

const maritalStatusChoices = [
  { id: "single", name: "Solteiro(a)" },
  { id: "married", name: "Casado(a)" },
  { id: "divorced", name: "Divorciado(a)" },
  { id: "widowed", name: "Viúvo(a)" },
  { id: "other", name: "Outro" },
];

const personTypeChoices = [
  { id: "PF", name: "Pessoa Física" },
  { id: "PJ", name: "Pessoa Jurídica" },
];

const brazilianStates = [
  { id: "AC", name: "Acre" },
  { id: "AL", name: "Alagoas" },
  { id: "AP", name: "Amapá" },
  { id: "AM", name: "Amazonas" },
  { id: "BA", name: "Bahia" },
  { id: "CE", name: "Ceará" },
  { id: "DF", name: "Distrito Federal" },
  { id: "ES", name: "Espírito Santo" },
  { id: "GO", name: "Goiás" },
  { id: "MA", name: "Maranhão" },
  { id: "MT", name: "Mato Grosso" },
  { id: "MS", name: "Mato Grosso do Sul" },
  { id: "MG", name: "Minas Gerais" },
  { id: "PA", name: "Pará" },
  { id: "PB", name: "Paraíba" },
  { id: "PR", name: "Paraná" },
  { id: "PE", name: "Pernambuco" },
  { id: "PI", name: "Piauí" },
  { id: "RJ", name: "Rio de Janeiro" },
  { id: "RN", name: "Rio Grande do Norte" },
  { id: "RS", name: "Rio Grande do Sul" },
  { id: "RO", name: "Rondônia" },
  { id: "RR", name: "Roraima" },
  { id: "SC", name: "Santa Catarina" },
  { id: "SP", name: "São Paulo" },
  { id: "SE", name: "Sergipe" },
  { id: "TO", name: "Tocantins" },
];

const normalizarCidade = (nome: string) =>
  nome.normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim().toLowerCase().replace(/\s+/g, " ");

export const ContactAddressInputs = ({
  requireDocumentAndAddress,
  comTitulo = true,
}: {
  requireDocumentAndAddress: boolean;
  comTitulo?: boolean;
}) => {
  const { watch, setValue } = useFormContext();
  const selectedState = watch("details.state");
  const zipcode = watch("details.zipcode");
  const [cities, setCities] = useState<{ id: string; name: string }[]>([]);
  const [loadingCities, setLoadingCities] = useState(false);
  // Só com a lista do IBGE indisponível a cidade pode ser digitada livre; com a
  // lista carregada, escolhe-se dela (evita "Varzea grande", "VG"... que o robô
  // do Servopa usaria tal qual quando a consulta de CEP de lá falha).
  const [ibgeFalhou, setIbgeFalhou] = useState(false);
  const [loadingAddress, setLoadingAddress] = useState(false);

  const selectedCity = watch("details.city");

  // Lista de cidades do IBGE. O IBGE cai/fica lento com
  // frequência: antes, a lista vinha vazia e não havia como escolher a cidade,
  // e o campo ficava `disabled` enquanto carregava — o react-hook-form descarta
  // campo desabilitado no submit, então o contato era salvo SEM cidade e a venda
  // travava em "Preencha o campo obrigatório: Cidade" (03/11/2026).
  useEffect(() => {
    if (!selectedState) {
      setCities([]);
      setIbgeFalhou(false);
      return;
    }

    // `cancelado`: o estado mudou ou o form fechou — descarta a resposta velha.
    // O timeout aborta a busca e cai no catch (lista vazia, digitação livre).
    let cancelado = false;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);

    const fetchCities = async () => {
      setLoadingCities(true);
      setIbgeFalhou(false);
      try {
        const response = await fetch(
          `https://servicodados.ibge.gov.br/api/v1/localidades/estados/${selectedState}/municipios`,
          { signal: controller.signal }
        );
        if (!response.ok) throw new Error(`IBGE respondeu ${response.status}`);
        const data = await response.json();
        if (!Array.isArray(data)) throw new Error("IBGE: resposta inesperada");
        const cityList = data.map((city: { id: number; nome: string }) => ({
          id: city.nome,
          name: city.nome,
        }));
        if (!cancelado) setCities(cityList);
      } catch (error) {
        if (cancelado) return;
        console.error("Error fetching cities:", error);
        setCities([]);
        setIbgeFalhou(true);
      } finally {
        clearTimeout(timeout);
        if (!cancelado) setLoadingCities(false);
      }
    };

    fetchCities();
    return () => {
      cancelado = true;
      clearTimeout(timeout);
      controller.abort();
    };
  }, [selectedState]);

  // A cidade gravada (ou vinda do ViaCEP) sempre aparece como opção, mesmo sem
  // a lista do IBGE ou com grafia diferente dela.
  const cityChoices = useMemo(() => {
    const atual = typeof selectedCity === "string" ? selectedCity.trim() : "";
    if (!atual || cities.some((c) => c.id === atual)) return cities;
    return [{ id: atual, name: atual }, ...cities];
  }, [cities, selectedCity]);

  // Com a lista carregada, cidade gravada com outra grafia ("varzea grande",
  // digitada num dia sem IBGE) vira a grafia oficial, se bater sem acento/caixa.
  useEffect(() => {
    if (cities.length === 0 || typeof selectedCity !== "string") return;
    const atual = selectedCity.trim();
    if (!atual || cities.some((c) => c.id === atual)) return;
    const chave = normalizarCidade(atual);
    const oficial = cities.find((c) => normalizarCidade(c.name) === chave);
    if (oficial) setValue("details.city", oficial.id, { shouldDirty: true });
  }, [cities, selectedCity, setValue]);

  const criarCidade = (texto?: string) => {
    const nome = (texto || "").trim();
    if (!nome) return undefined;
    const choice = { id: nome, name: nome };
    setCities((prev) => (prev.some((c) => c.id === nome) ? prev : [choice, ...prev]));
    return choice;
  };

  // Fetch address from ViaCEP when zipcode changes
  useEffect(() => {
    if (!zipcode || zipcode.length < 8) return;

    const fetchAddress = async () => {
      setLoadingAddress(true);
      try {
        const cleanZipcode = zipcode.replace(/\D/g, "");
        const response = await fetch(
          `https://viacep.com.br/ws/${cleanZipcode}/json/`
        );
        const data = await response.json();
        
        if (!data.erro) {
          setValue("details.address", data.logradouro);
          setValue("details.neighborhood", data.bairro);
          setValue("details.city", data.localidade);
          setValue("details.state", data.uf);
        }
      } catch (error) {
        console.error("Error fetching address:", error);
      } finally {
        setLoadingAddress(false);
      }
    };

    fetchAddress();
  }, [zipcode, setValue]);

  return (
    <div className="flex flex-col gap-4">
      {comTitulo && <h6 className="text-lg font-semibold">Endereço</h6>}
      <TextInput 
        source="details.zipcode" 
        label="CEP"
        validate={requireDocumentAndAddress ? required() : undefined} 
        helperText={false}
        placeholder="00000-000"
        disabled={loadingAddress}
      />
      <TextInput 
        source="details.address" 
        label="Endereço"
        validate={requireDocumentAndAddress ? required() : undefined} 
        helperText={false}
        disabled={loadingAddress}
      />
      <TextInput 
        source="details.address_number" 
        label="Número"
        validate={requireDocumentAndAddress ? required() : undefined} 
        helperText={false} 
      />
      <TextInput 
        source="details.address_complement" 
        label="Complemento"
        helperText={false}
        placeholder="Apartamento, bloco, etc."
      />
      <TextInput 
        source="details.neighborhood" 
        label="Bairro"
        validate={requireDocumentAndAddress ? required() : undefined} 
        helperText={false}
        disabled={loadingAddress}
      />
      <AutocompleteInput
        source="details.state"
        label="Estado"
        choices={brazilianStates}
        helperText={false}
        align="start"
        side="bottom"
        validate={requireDocumentAndAddress ? required() : undefined}
        disabled={loadingAddress}
      />
      {/* Sem `disabled` durante o carregamento: campo desabilitado sai do submit
          (react-hook-form) e o contato era salvo sem cidade. */}
      <AutocompleteInput
        source="details.city"
        label="Cidade"
        choices={cityChoices}
        helperText={false}
        disabled={!selectedState}
        placeholder={
          loadingCities
            ? "Carregando cidades..."
            : ibgeFalhou
              ? "Digite a cidade"
              : "Buscar cidade"
        }
        {...(ibgeFalhou
          ? {
              onCreate: criarCidade,
              createLabel: "Lista do IBGE indisponível: digite a cidade",
              createItemLabel: (item: string) => `Usar "${item}"`,
            }
          : {})}
        align="start"
        side="bottom"
        validate={requireDocumentAndAddress ? required() : undefined}
      />
    </div>
  );
};

const ContactPersonalInformationInputs = ({
  requireDocumentAndAddress,
}: {
  requireDocumentAndAddress: boolean;
}) => {
  const translate = useTranslate();
  return (
    <div className="flex flex-col gap-4">
      <h6 className="text-lg font-semibold">
        {translate("atomic.contacts.personalInfo")}
      </h6>
      <RendaMensalInput obrigatoria={requireDocumentAndAddress} />
      <ContactChannelsInputs />
    </div>
  );
};

export const RendaMensalInput = ({ obrigatoria = false }: { obrigatoria?: boolean }) => (
  <MoedaFormInput
    source="details.monthly_income"
    label="Renda Mensal"
    validate={obrigatoria ? required() : undefined}
    helperText={false}
  />
);

/**
 * Celular obrigatório em todo contato desde a migration 240 (antes só o lead
 * exigia): é ele que o banco usa para não deixar a mesma pessoa em duas bases.
 * "( ) -" sem dígito conta como vazio, como no banco.
 */
const celularObrigatorio = [
  required(MENSAGEM_SEM_CELULAR),
  (valor: unknown) => (normalizarCelular(valor) ? undefined : MENSAGEM_SEM_CELULAR),
];

/** E-mail, celular e preferência: por onde se fala com a pessoa. */
export const ContactChannelsInputs = () => {
  const { getValues, setValue } = useFormContext();

  // set name based on email
  const handleEmailChange = (email: string) => {
    const name = getValues("details.name");
    if (name || !email) return;
    const localPart = email.split("@")[0];
    const inferredName = localPart.replace(/\./g, " ").trim();
    if (!inferredName) return;
    setValue("details.name", inferredName.charAt(0).toUpperCase() + inferredName.slice(1));
  };

  const handleEmailPaste: ClipboardEventHandler<
    HTMLTextAreaElement | HTMLInputElement
  > = (e) => {
    const email = e.clipboardData?.getData("text/plain");
    handleEmailChange(email);
  };

  const handleEmailBlur = (
    e: FocusEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    const email = e.target.value;
    handleEmailChange(email);
  };

  return (
    <>
      <TextInput
        source="details.email"
        label="Email"
        helperText={false}
        validate={email()}
        onPaste={handleEmailPaste}
        onBlur={handleEmailBlur}
      />
      <TextInput
        source="details.phone"
        label="Celular"
        helperText={false}
        validate={celularObrigatorio}
        placeholder="(00) 00000-0000"
      />
      <SelectInput
        source="details.contact_preference"
        label="Preferência de Contato"
        choices={contactPreferenceChoices}
        helperText={false}
        defaultValue="email"
      />
    </>
  );
};

const contactPreferenceChoices = [
  { id: "email", name: "Email" },
  { id: "phone", name: "Telefone" },
  { id: "whatsapp", name: "WhatsApp" },
];

const ContactMiscInputs = () => (
  <div className="flex flex-col gap-4">
    <h6 className="text-lg font-semibold">Misc</h6>
    {/* `details.background` morava aqui. Foi para o bloco "Perfil" de
        crm/camposDoLead.tsx, que os dois formulários montam — repetir o campo
        nos dois lugares faria a mesma pergunta duas vezes na mesma tela. O
        responsável também saiu: é o `ResponsavelInput` de crm/responsavel.tsx,
        que troca o dono pelo banco (migration 211) em vez de gravar user_id. */}
  </div>
);
